// 产品对比 C6
import React, { FC, useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'
import { useMobileDetector } from '@hooks/use-mobile-detector'
import { IComparePanel, ComparePanelCardProps, paddingTopClassMap, paddingBottomClassMap } from '@common/pd-meta-components-types'
import PdImage from '@components/pd-image/pd-image'
import PdMetaContactUsButton from '@components/pd-meta-contactus-button/pd-meta-contactus-button'
import TitleAndDesc from '../../title-and-desc'

const ImgTabsCom: FC<{ data: ComparePanelCardProps['colors']; defaultActiveIndex?: number; showColor?: boolean }> = ({
  data = [],
  defaultActiveIndex = 0,
  showColor = true,
}) => {
  const [activeIndex, setActiveIndex] = useState<number>(0)

  const imgSrc = useMemo<string>(() => {
    return data[activeIndex]?.imgUrl || ''
  }, [data, activeIndex])

  useEffect(() => {
    setActiveIndex(defaultActiveIndex)
  }, [defaultActiveIndex])

  return (
    <div className={['flex flex-col items-center'].join(' ')}>
      <div className={['transition-opacity', 'w-30 h-142px', 'laptop:w-240px laptop:h-284px'].join(' ')}>
        <PdImage src={imgSrc} enableMask={false} object="cover" width={'100%'} height={'100%'} />
      </div>

      {showColor && (
        <div className={['flex justify-center gap-2 mt-12px', 'laptop:mt-24px laptop:gap-4'].join(' ')}>
          {data.map((i, index) => (
            <div
              className={[
                'border rounded-full flex items-center justify-center',
                'rounded-1 w-18px h-18px',
                'laptop:rounded-2 laptop:w-10 laptop:h-10',
                activeIndex === index ? ' border-b-blue' : 'border-bd-secondary',
              ].join(' ')}
              key={index}
            >
              <span
                className={[
                  'rounded-full cursor-pointer shadow-[0px_0.5px_1px_1px_rgba(0,0,0,0.26)_inset]',
                  'w-14px h-14px',
                  'laptop:w-8 laptop:h-8',
                  // i.color === 1 ? 'bg-white' : '',
                  // i.color === 0 ? 'bg-black/90' : '',
                ].join(' ')}
                onClick={() => {
                  setActiveIndex(index)
                }}
                style={{ backgroundColor: i.color }}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const ComparePanelCard: FC<
  ComparePanelCardProps & {
    selectDataList: ComparePanelCardProps[]
    index: number
    onSelect: (title: string, index: number) => void
    showColor?: boolean
  }
> = ({ selectDataList, title, colors, introduceArr, defaultActiveColor, onSelect, index, showColor = true }) => {
  const defaultActiveIndex = useMemo<number>(() => {
    const activeIndex: number = colors?.findIndex((item) => item.color === defaultActiveColor) || 0

    return activeIndex === -1 ? 0 : activeIndex
  }, [colors, defaultActiveColor, title])

  return (
    <div className={['flex-none flex flex-col justify-center align-middle', 'w-36', 'laptop:w-290px'].join(' ')}>
      <select
        className={clsx(
          'sticky top-16 z-20 box-border',
          'text-center font-semibold w-full border border-bd-secondary',
          'py-2 mb-2 text-12 rounded-lg',
          'laptop:py-4 laptop:mb-4 laptop:text-20 laptop:rounded-2xl',
          'text-fc-primary bg-container-primary'
        )}
        value={title}
        onChange={(v) => onSelect(v.target.value, index)}
      >
        {selectDataList.map((col, index) => (
          <option key={index} value={col.title}>
            {col.title}
          </option>
        ))}
      </select>

      <div className={[''].join(' ')}>
        <div className={['w-full p-3', 'laptop:p-6'].join(' ')}>
          {!!colors?.length && (
            <ImgTabsCom data={colors} defaultActiveIndex={defaultActiveIndex} showColor={showColor} />
          )}

          <div className={[''].join(' ')}>
            {!!introduceArr?.length &&
              introduceArr.map((item, index) => (
                <div className={['mt-20px', 'laptop:mt-40px'].join(' ')} key={index}>
                  <div className={['font-normal text-12 text-center', 'laptop:text-14', 'text-fc-primary'].join(' ')}>
                    {item.title}
                  </div>
                  <div
                    className={['font-normal text-12 text-center', 'mt-1 laptop:text-14', 'text-fc-secondary'].join(
                      ' '
                    )}
                  >
                    {item.desc}
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  )
}

const ComparePanel: FC<IComparePanel> = ({ title, desc, dataList = [], robotCode, theme, showColor = true, paddingTop = 'px120', paddingBottom = 'px120' }) => {
  const isMobileDevice = useMobileDetector()
  const [showDataList, setShowDataList] = useState<IComparePanel['dataList']>([])

  const handleSelect = (title: string, index: number) => {
    const titleIndex = showDataList.findIndex((item) => item.title === title)

    if (titleIndex !== -1) {
      setShowDataList((prev) => {
        const originList = prev.slice()
        const temp = originList[titleIndex]
        originList[titleIndex] = originList[index]
        originList[index] = temp

        return originList
      })
    } else {
      setShowDataList((prev) => {
        const originList = prev.slice()
        const temp = dataList.find((item) => item.title === title)
        if (temp) {
          originList[index] = temp
        }

        return originList
      })
    }
  }

  useEffect(() => {
    setShowDataList(dataList.slice(0, isMobileDevice ? 2 : 3))
  }, [isMobileDevice])

  return (
    <div className={clsx('w-full mx-auto', paddingTopClassMap[paddingTop], paddingBottomClassMap[paddingBottom], `pd-theme-${theme} bg-container-primary`)}>
      <TitleAndDesc title={title} desc={desc} />
      <div className={clsx('w-full')}>
        <div className={clsx('w-fit mx-auto flex gap-4 justify-center items-start')}>
          {showDataList?.map((item, index) => (
            <ComparePanelCard
              key={index}
              {...item}
              index={index}
              selectDataList={dataList}
              showColor={showColor}
              onSelect={(title) => {
                handleSelect(title, index)
              }}
            />
          ))}
        </div>
      </div>

      <div className={['w-full flex justify-center', 'mt-32px', 'laptop:mt-64px'].join(' ')}>
        {/* Contact us button */}
        <PdMetaContactUsButton
          className={['!w-327px h-15', 'laptop:!w-75 laptop:h-68px rounded-[4px]'].join(' ')}
          robotCode={robotCode}
        />
      </div>
    </div>
  )
}
export default ComparePanel
